import { Injectable } from '@angular/core';
import { Observable, throwError, of } from 'rxjs';
import {
    AppProfileData,
    AudienceDedupCheckRequest,
    AudienceDedupCheckResponse,
    BroadcastFiltersData,
    BroadcastFormData,
    BroadcastFormResolverData,
    BroadcastPreviewTableDataModel,
    BroadcastSentUser,
    GeneralListDataResponse,
    GetBroadcastHistory,
    ISegmentAnalyzerData,
    ISegmentAnalyzerRiskAcceptPayload,
    MultiSchedulerData,
    PreviewSheetData,
    ScheduledEditBroadcast,
    ScheduledSendBroadcast,
    SendBroadcast,
    SendBroadcastPreviewData,
    SendBroadcastSuccess,
    SendWelcomeMessage,
    SuggestMessageResponse,
    Translated
} from '@app/models';
import { HttpService } from '@app/core/http.service';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { OrganizationList, SeniorCenter, Timezone } from '@app/models/organization.model';
import { CommonService } from '@app/core/common.service';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { GeneralDataResponse } from '@app/models/user.model';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import {
    BroadcastPreviewLabels,
    BROADCAST_MESSAGES,
    BroadcastStatus,
    ChannelLabels,
    DATE_TIME_FORMATS,
    AnalyticLogLabels
} from '@app/shared';
import { AnalyticService } from '@app/core/analytic.service';
import * as moment from 'moment';
import { appendCustomFieldsToTableData, DuplicateCommunicationData, removeFalsyValues } from '@app/helpers';
import { MomentTimezonePipe } from '@app/shared/pipes/utcToTimezone.pipe';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

@Injectable({
    providedIn: 'root'
})
export class BroadcastService {
    constructor(
        private readonly _httpS: HttpService,
        private readonly _commonS: CommonService,
        private readonly _momentTimezonePipe: MomentTimezonePipe,
        private readonly sanitizer: DomSanitizer
    ) {}

    private _broadcastConfirmation: SendBroadcastSuccess | null = null;
    private _broadcastErrorResponse: HttpErrorResponse | null = null;
    private _broadcastPayloadData: SendBroadcast | null = null;
    private _scheduledBroadcastPayloadData: ScheduledSendBroadcast | null = null;

    get broadcastPayloadData() {
        return this._broadcastPayloadData;
    }

    setBroadcastPayloadData(data: SendBroadcast | null) {
        this._broadcastPayloadData = data;
    }

    get scheduledBroadcastPayloadData() {
        return this._scheduledBroadcastPayloadData;
    }

    setScheduledBroadcastPayloadData(data: ScheduledSendBroadcast | null) {
        this._scheduledBroadcastPayloadData = data;
    }

    getOrganisationInfo(): Observable<GeneralDataResponse<SeniorCenter[]>> {
        const orgId = this._commonS.selectedOrgId;
        return this._httpS.getSeniorcenterByOrgId(orgId);
    }

    sendBroadcastPreview(body: ScheduledSendBroadcast): Observable<SendBroadcastPreviewData> {
        return this._httpS.sendBroadcastPreview(body).pipe(
            map(response => {
                if (body?.schedule_time?.length && !response.error) {
                    response.data.schedule_time = body.schedule_time;
                }
                return { ...response };
            })
        );
    }

    buildDedupCheckRequest(payload: ScheduledSendBroadcast): AudienceDedupCheckRequest {
        return {
            org_id: payload.orgId,
            outreach_type: 'screening',
            // BH-7556: raw @firstname-form composed text (formControls.message.value) — the
            // same value sent to the send endpoint; per-recipient substitution happens later.
            message: payload.message,
            audience: payload.audience ?? [],
            centers: payload.centers ?? [],
            advanceFilters: payload.advanceFilters,
            customFieldsFilters: payload.customFieldsFilters,
            interest_ids: payload.interest_ids?.length ? payload.interest_ids : undefined,
            parameters: { individual: payload.parameters?.individual },
            orgLabels: payload.orgLabels?.length ? payload.orgLabels : undefined
        };
    }

    checkAudienceDedup(payload: ScheduledSendBroadcast): Observable<AudienceDedupCheckResponse | null> {
        return this._commonS.getAudienceDedupCheck(this.buildDedupCheckRequest(payload)).pipe(
            catchError(() => of(null))
        );
    }

    sendBroadcast(body: ScheduledSendBroadcast): Observable<SendBroadcastSuccess> {
        if(!body?.tags?.length) {
            body.tags = []
        }
        if (body.attachment && !body.attachment?.hasOwnProperty('value')) {
            const formData = new FormData();

            Object.keys(body).forEach(key => {
                const value = body[key as keyof ScheduledSendBroadcast];

                if (key === 'attachment' && body.attachment) {
                    formData.append('attachment', body.attachment, body.attachment.name);
                } else if (Array.isArray(value)) {
                    // Append array items directly
                    value.forEach(item => {
                        formData.append(key, item); // Add each item individually
                    });
                } else if (typeof value === 'object' && value !== null) {
                    formData.append(key, JSON.stringify(value)); // For other objects
                } else {
                    formData.append(key, value as string | Blob);
                }
            });

            return this._httpS.sendBroadcast(formData).pipe(
                tap(data => (this._broadcastConfirmation = data)),
                catchError(error => {
                    this._broadcastErrorResponse = error;
                    return throwError(error);
                })
            );
        } else {
            // Make sure that the attachment property is removed
            delete body.attachment;
            // If no attachment, send broadcast
            return this._httpS.sendBroadcast(body).pipe(
                tap(data => (this._broadcastConfirmation = data)),
                catchError(error => {
                    this._broadcastErrorResponse = error;
                    return throwError(error);
                })
            );
        }
    }

    getSuggestMessage(prompt: string, broadcastType: string = 'broadcast'): Observable<SuggestMessageResponse> {
        return this._httpS.getSuggestMessage(prompt, broadcastType).pipe(map(response => response.data));
    }

    segmentAnalyzerFullCheck(message: string, languages: string[]): Observable<ISegmentAnalyzerData> {
        return this._httpS.segmentAnalyzerFullCheck({ message, languages }).pipe(map(response => response.data));
    }

    segmentAnalyzerRiskAccept(body: ISegmentAnalyzerRiskAcceptPayload): Observable<GeneralDataResponse<any>> {
        return this._httpS.segmentAnalyzerRiskAccept(body);
    }
    
    sendWelcomeMessage(body: SendWelcomeMessage): Observable<SendBroadcastSuccess> {
        return this._httpS.sendWelcomeMessage(body).pipe(
            tap(data => (this._broadcastConfirmation = data)),
            catchError(error => {
                this._broadcastErrorResponse = error;
                return throwError(error);
            })
        );
    }

    /**
     * Broadcast Logs
     * @param orgID To Get Broadcast history by organization
     * @param params To send the filters of the table
     * @returns getBroadcastHistory as observable
     */
    getBroadcastHistory(orgId: number, params?: HttpParams, isRefresh: boolean = false): Observable<GetBroadcastHistory> {
        let newParams = params || new HttpParams();
        newParams = newParams.set('scheduled', false);
        return this._httpS.getBroadcastHistory(orgId, newParams, isRefresh).pipe(
            map(response => {
                return {
                    ...response,
                    data: {
                        ...response.data,
                        rows: [
                            ...response.data.rows.map(record => {
                                let snapValue = '';
                                if (record?.parameters_summary?.interests) console.log(record.parameters_summary);
                                if (record.parameters_summary?.snap_eligibility || record.parameters_summary?.snap_eligible)
                                    snapValue = `SNAP Eligible: ${record.parameters_summary.snap_eligibility || record.parameters_summary.snap_eligible}`;

                                if (record.parameters_summary?.snap_enrollment)
                                    snapValue = `${snapValue ? snapValue + ', ' : ''}SNAP Enrollment: ${
                                        record.parameters_summary.snap_enrollment
                                    }`;

                                if (record.parameters_summary?.snap_interest)
                                    snapValue = `${snapValue ? snapValue + ', ' : ''}SNAP Interest: ${
                                        record.parameters_summary.snap_interest
                                    }`;
                                return {
                                    ...record,
                                    parameter_summary: {
                                        ...record.parameters_summary,
                                        snap_value: snapValue
                                    }
                                };
                            })
                        ]
                    }
                };
            })
        );
    }

    downloadBroadcastAttachment(body: any): Observable<any> {
        return this._httpS.downloadBroadcastAttachment(body);
    }

    getBroadcastSentUsers(
        broadcastId: number,
        timezone: Timezone,
        params?: HttpParams
    ): Observable<GeneralListDataResponse<BroadcastSentUser>> {
        return this._httpS.getBroadcastSentUsers(broadcastId, params).pipe(
            map(response => {
                response = {
                    ...response,
                    data: {
                        ...response.data,
                        rows: [
                            ...response.data.rows.map(record => {
                                let modifiedRecord: BroadcastSentUser = {
                                    ...record,
                                    delivery_status: {
                                        ...BroadcastStatus.completed,
                                        value: `${record.delivery_status}`
                                    },
                                    final_status: {
                                        ...BroadcastStatus.completed,
                                        value: `${record.final_status}`
                                    },
                                    timestamp: record.timestamp?.length
                                        ? this._momentTimezonePipe.transform(
                                              record.timestamp,
                                              timezone.timezone_identifier,
                                              DATE_TIME_FORMATS.mediumDateTime
                                          ) || record.timestamp
                                        : record.timestamp,
                                    channel: ChannelLabels.hasOwnProperty(record.channel) ? ChannelLabels[record.channel] : record.channel
                                };
                                return modifiedRecord;
                            })
                        ]
                    }
                };
                return response;
            })
        );
    }

    /**
     * Scheduled Broadcast Logs
     * @param orgID To Get Scheduled Broadcast by organization
     * @param params To send the filters of the table
     * @returns getBroadcastHistory as observable
     */
    getScheduledBroadcast(orgId: number, params?: HttpParams): Observable<GetBroadcastHistory> {
        let newParams = params || new HttpParams();
        newParams = newParams.set('scheduled', true);
        return this._httpS.getBroadcastHistory(orgId, newParams);
    }

    getBroadcastDataById(id: number): Observable<BroadcastFormResolverData> {
        return this._httpS.getBroadcastDataById(id).pipe(
            map(response => {
                const convertedData: BroadcastFormData = this.convertResponseToFormData(response.data);
                let resolvedData = new BroadcastFormResolverData(id, convertedData, this.convertFormDataToFilterData(convertedData));
                let hasEligible = convertedData.eligible_count && convertedData.eligible_count > 0;
                resolvedData.hasNoEligible = !hasEligible;
                return resolvedData;
            })
        );
    }

    updateBroadcast(id: number, data: ScheduledSendBroadcast): Observable<GeneralDataResponse<any>> {
        if(!data?.tags?.length) {
            data.tags = []
        }
        if (data.attachment && !data.attachment?.hasOwnProperty('value') && data.attachment?.size !== 0) {
            const formData = new FormData();

            Object.keys(data).forEach(key => {
                const value = data[key as keyof ScheduledSendBroadcast];

                if (key === 'attachment' && data.attachment) {
                    formData.append('attachment', data.attachment, data.attachment.name);
                } else if (Array.isArray(value)) {
                    value.forEach(item => {
                        formData.append(key, item);
                    });
                } else if (typeof value === 'object' && value !== null) {
                    formData.append(key, JSON.stringify(value));
                } else {
                    formData.append(key, value as string | Blob);
                }
            });

            return this._httpS.updateBroadcast(id, formData);
        }

        delete data.attachment;
        return this._httpS.updateBroadcast(id, data);
    }

    deleteBroadcast(id: number): Observable<GeneralDataResponse<any>> {
        return this._httpS.deleteBroadcast(id);
    }

    get broadcastConfirmationDetail() {
        return this._broadcastConfirmation;
    }

    get broadcastError() {
        return this._broadcastErrorResponse;
    }

    get timezone(): Timezone | null {
        const appProfileData: AppProfileData = this._commonS.appProfileDataAsValue;

        //get current center timezone details
        if (appProfileData?.currentOrganization?.timezone) {
            return appProfileData?.currentOrganization?.timezone;
        } else {
            return null;
        }
    }

    //HELPER CLASSES

    convertResponseToFormData(response: ScheduledEditBroadcast): BroadcastFormData {
        if (response?.member_details?.length) {
            response = {
                ...response,
                member_details:
                    response.member_details.map(member => {
                        return {
                            ...member,
                            Programs: {
                                id: member.ClientCenters?.map((center: any) => center?.senior_center_id || center?.id) || [],
                                name: member.ClientCenters?.map((center: any) => center?.SeniorCenter?.name)?.join(', ') || null
                            }
                        };
                    }) || []
            };
        }
        let data: BroadcastFormData = {
            ...response,
            audience: response.audience.split(', '),
            channels: response.channels.split(', '),
            member_details: response.member_details || []
        };
        if (response.tags?.length) {
            data.tags = [...response.tags].sort((a, b) => a - b);
        }
        return data;
    }

    //**property orders are must be followed as follows */
    convertFormDataToFilterData(formData: BroadcastFormData): BroadcastFiltersData {
        let broadcastData: BroadcastFiltersData = {
            audience: formData.audience,
            centers: [],
            individual: formData.parameters.individual,
            orgLabels: formData?.org_label_ids || [],
        };
        if (Object.keys(formData.advanceFilters ?? {}).length > 0) {
            broadcastData = {
                ...broadcastData,
                advanceFilters: formData.advanceFilters
            };
        }
        if (!formData.parameters.individual) {
            let paramsData = { 
                self_caregiver: formData.parameters.self_caregiver, 
                ph_interested: formData.parameters.ph_interested, 
                ph_consented: formData.parameters.ph_consented, 
                ph_info_provided: formData.parameters.ph_info_provided, 
                snap_eligibility: formData.parameters.snap_eligibility, 
                snap_enrollment: formData.parameters.snap_enrollment, 
                snap_interest: formData.parameters.snap_interest
            };
            broadcastData = {
                ...broadcastData,
                audience: formData.senior_centers,
                centers: formData.audience, 
                ...removeFalsyValues(paramsData)
            };
            if (formData.interest_ids?.length) broadcastData.interest_ids = formData.interest_ids?.map(id => id.toString()) || [];
        }
        return broadcastData;
    }

    combineFiltersInPayload(
        orgId: number,
        formControls: { [key: string]: AbstractControl },
        filterValues: BroadcastFiltersData
    ): ScheduledSendBroadcast {
        let body: ScheduledSendBroadcast = {
            message: formControls.message.value,
            channels: this.getChannels(formControls.channels.value),
            orgId: orgId,
            centers: filterValues.centers,
            parameters: {
                individual: filterValues.individual,
                self_caregiver: filterValues.self_caregiver,
                translated: [],
                ph_consented: filterValues.ph_consented,
                ph_info_provided: filterValues.ph_info_provided,
                ph_interested: filterValues.ph_interested,
                snap_eligibility: filterValues.snap_eligibility,
                snap_enrollment: filterValues.snap_enrollment,
                snap_interest: filterValues.snap_interest
            },
            audience: filterValues.audience,
            interest_ids: [],
            attachment: formControls.attachment.value,
            orgLabels: filterValues.orgLabels || [],
            advanceFilters: filterValues.advanceFilters || {}
        };

        if (filterValues.interest_ids?.length) {
            body.interest_ids = filterValues.interest_ids.map(id => parseInt(id));
        }
        if (body.parameters.individual) {
            body.audience = body.audience.map(id => `'${id}'`);
        }
        return body;
    }

    /** If auto-tranlate is off for any language, get its translated message and the language name */
    getTranlations(orgLanguages: string[], translationValues: any): { translated: Translated[] } | null {
        const translations: Translated[] = [];
        if (!orgLanguages?.length) return null;
        Object.keys(translationValues).forEach(key => {
            if (!translationValues[key].autoTranslate) {
                translations.push({
                    language: key,
                    message: translationValues[key].message
                });
            }
        });

        return translations.length ? { translated: translations } : null;
    }

    /**
     * Channels to send broadcast
     * @param channels : object
     * @returns string[] - ['sms', 'voice', 'email'], any one or all
     */
    getChannels(channels: any): string[] {
        let channelsArr: string[] = [];
        Object.keys(channels).forEach(channel => {
            if (channels[channel]) {
                let chl: string = '';
                switch (channel) {
                    case 'sms':
                        chl = 'SMS';
                        break;
                    case 'voice':
                        chl = 'Voice Call';
                        break;
                    case 'email':
                        chl = 'E-Mail';
                        break;
                    default:
                        chl = channel;
                        break;
                }
                channelsArr.push(chl);
            }
        });
        return channelsArr;
    }

    getChannelsEventValue(channels: any): string {
        let channelsFormData = this.getChannels(channels);
        return !channelsFormData?.length ? '' : channelsFormData.map(ch => ch.toUpperCase()).join('/');
    }

    /** to validate the form group */
    checkFormGroup() {
        return (formGroup: UntypedFormGroup) => {
            const channelValues = formGroup.value;
            let valid = false;
            for (let key in channelValues) {
                valid = valid || channelValues[key];
            }
            if (!valid) return { notValid: true };
            else return;
        };
    }

    private getBroadcastPreviewContent(
        tableData: BroadcastPreviewTableDataModel[], 
        previewResponse: SendBroadcastPreviewData, 
        timezone_identifier: string
    ) {
        let tableContents: any = previewResponse.data;

        let contentKey: any = BroadcastPreviewLabels;
        if (tableContents.eligiblecount !== undefined) {
            tableData = [
                ...tableData,
                {
                    name: contentKey['eligiblecount'],
                    value: `${tableContents.eligiblecount}`,
                    isHighlighted: true
                }
            ];
        }
        let filterLangFlag = false;
        if (tableContents.advanceFilters?.languages?.length) {
            tableData.push({
                name: 'Language(s)',
                value: tableContents.advanceFilters.languages.join(', ')
            });
            filterLangFlag = true;
        }
        for (let key in contentKey) {
            if (key === 'eligiblecount') continue;
            if (key === 'languages' && filterLangFlag) continue;
            let value;
            if (['schedule_time'].includes(key) && tableContents[key]?.length) {
                value = this._momentTimezonePipe.transform(
                    tableContents[key],
                    timezone_identifier,
                    DATE_TIME_FORMATS.mediumDateTime
                );
            } else {
                value = tableContents[key];
            }
            if (tableContents[key] || tableContents[key] === 0) {
            tableData = [
                ...tableData,
                {
                    name: contentKey[key],
                    value: `${value}`,
                    isHighlighted: false
                }
            ];
        }
        }
        if (tableContents.advanceFilters && Object.keys(tableContents.advanceFilters).length > 0) {
            if (tableContents.advanceFilters.gender?.length) {
                const genders = tableContents.advanceFilters.gender.map((g: string) => g.charAt(0).toUpperCase() + g.slice(1));
                tableData.push({ name: 'Gender', value: genders.join(', ') });
            }
            if (tableContents.advanceFilters.race?.length) {
                const raceValues = tableContents.advanceFilters.race.length > 1
                    ? tableContents.advanceFilters.race.map((race: string) => `[${race}]`)
                    : tableContents.advanceFilters.race;
                tableData.push({ name: 'Race/Ethnicity', value: raceValues.join(', ') });
            }
            if (tableContents.advanceFilters.disability?.length) {
                tableData.push({ name: 'Disability Status', value: tableContents.advanceFilters.disability.join(', ') });
            }
            if (tableContents.advanceFilters.veteran?.length) {
                tableData.push({ name: 'Veteran Status', value: tableContents.advanceFilters.veteran.join(', ') });
            }
            if (tableContents.advanceFilters.zipcodes?.length) {
                tableData.push({ name: 'Zip Code', value: tableContents.advanceFilters.zipcodes.join(', ') });
            }
            if (tableContents.advanceFilters.age) {
                const age = tableContents.advanceFilters.age;
                tableData.push({ name: 'Age', value: `Min: ${age.min}, Max: ${age.max}` });
            }
        }
        return tableData;
    }

    private getBroadcastPreviewAttachments(
        tableData: BroadcastPreviewTableDataModel[], 
        requestData: ScheduledSendBroadcast
    ) {
        let attachmentData: BroadcastPreviewTableDataModel[] = [];
        // Check if attachment is present
        if (tableData?.length && !tableData.find(data => data.name == 'Attachments') && requestData.attachment) {
            if (!requestData.attachment.hasOwnProperty('value')) {
                const blobUrl = URL.createObjectURL(requestData.attachment);
                const safeUrl = this.sanitizer.bypassSecurityTrustUrl(blobUrl);
                attachmentData = [
                    {
                        name: 'Attachments',
                        value: requestData.attachment.name,
                        url: safeUrl,
                        fileType: requestData.attachment.type,
                        fileName: requestData.attachment.name,
                        fileSize: requestData.attachment.size / (1024 * 1024),
                        attachmentUrl: requestData.attachmentUrl
                    },
                ];
            }
        }
        return attachmentData;
    }

    private handleBroadcastPreviewWarnings(
        previewResponse: SendBroadcastPreviewData, 
        currentTimezone: Timezone, 
        isEligible: any, 
        hasSchedule: any
    ) {
        let warnValues: any[] = [];

        if (!isEligible) {
            warnValues.push({
                value: !hasSchedule ? BROADCAST_MESSAGES.SEND_NO_ELIGIBLE_ERR : BROADCAST_MESSAGES.SCH_CREATE_NO_ELIGIBLE_WARN,
                isErrorType: !hasSchedule
            });
        }
        if (previewResponse?.data?.duplicate_comm) {
            const dupCommData = new DuplicateCommunicationData(currentTimezone, previewResponse.data.duplicate_comm);
            const dupWarnMsgs = dupCommData.parseDuplicateWarnData();
            if (dupWarnMsgs?.list?.length) {
                warnValues.push(dupWarnMsgs);
            }
        }

        return warnValues;
    }

    applyBroadcastInPreviewData(
        previewResponse: SendBroadcastPreviewData,
        requestData: ScheduledSendBroadcast,
        currentTimezone: Timezone,
        isUpdate: boolean = false,
        isLimitExhaust: boolean = false,
        customFieldsMeta: any[] = []
    ): PreviewSheetData {
        let isEligible = null;
        let hasSchedule = null;
        let warnValues: any[] = [];
        let tableData: {
            name: string;
            value: string;
            url?: SafeUrl;
            fileType?: string;
            fileName?: string;
            fileSize?: number;
            attachmentUrl?: string;
            isHighlighted?: boolean;
        }[] = [];

        if (isLimitExhaust) {
            hasSchedule = !!requestData?.schedule_time?.length;
            tableData = [];
            isEligible = false;
            warnValues = [
                {
                    value: BROADCAST_MESSAGES.MOBILE_FREEMIUM_EXHAUST_PREVIEW_COMM_WARN,
                    isErrorType: true
                }
            ];
        } else {
            tableData = [
                ...tableData,
                ...this.getBroadcastPreviewContent(
                    [...tableData], 
                    previewResponse, 
                    currentTimezone.timezone_identifier
                )
            ];
            tableData = [
                ...this.getBroadcastPreviewAttachments(
                    [...tableData], 
                    {...requestData}
                ), 
                ...tableData
            ];

            isEligible = previewResponse.data?.eligiblecount && previewResponse.data.eligiblecount > 0;
            hasSchedule = previewResponse.data?.schedule_time?.length;
            warnValues = [
                ...warnValues, 
                ...this.handleBroadcastPreviewWarnings(
                    {...previewResponse},
                    currentTimezone, 
                    isEligible, 
                    hasSchedule
                )
            ];
        }

        appendCustomFieldsToTableData(
            (previewResponse?.data as any)?.customFieldsFilters,
            customFieldsMeta,
            tableData
        );

        let sendSchTitleLabel = !hasSchedule ? 'Send Broadcast' : 'Schedule Broadcast';
        let sendSchActionLabel = !hasSchedule ? 'SEND NOW' : 'SCHEDULE';
        let previewData: PreviewSheetData = {
            title: isUpdate ? 'Change Broadcast' : sendSchTitleLabel,
            subtitle: BROADCAST_MESSAGES.PREV_SHEET_ST,
            warnMessage: warnValues?.length ? warnValues : undefined,
            previewData: {
                title: '',
                subtitle: isLimitExhaust ? undefined : requestData.message,
                tableContents: tableData
            },
            actionL: { value: 'CANCEL' },
            actionR: {
                value: isUpdate ? 'UPDATE' : sendSchActionLabel,
                disabled: isLimitExhaust ? true : !isEligible && !hasSchedule
            },
            timezone: currentTimezone
        };
        return previewData;
    }

    /**FORM STATUS */
    getFormStatusByValue(value: string) {
        if (!value?.length) return null;
        switch (value) {
            case BroadcastStatus.completed.value:
                return BroadcastStatus.completed;

            case BroadcastStatus.inProgress.value:
                return BroadcastStatus.inProgress;

            case BroadcastStatus.queued.value:
            case BroadcastStatus.queued.label:
                return { ...BroadcastStatus.queued, value: BroadcastStatus.queued.label };

            case BroadcastStatus.error.value:
                return BroadcastStatus.error;

            case BroadcastStatus.cancelled.value:
                return BroadcastStatus.cancelled;

            default:
                return { ...BroadcastStatus.error, value: 'Failed' };
        }
    }

    isBroadcastFailed(value: string | null | undefined): boolean {
        if (!value?.length) return false;
        return [BroadcastStatus.error.value, BroadcastStatus.failed.value].includes(value);
    }

    mergePreviewStartDate(previewStartDate: { rowNumber: string; commDate: string }[]): { rowNumber: string; commDate: string }[] {
        //PREVIEW REQ: Removes duplicate preview start date
        if (previewStartDate?.length > 1) {
            let orginalComm: { rowNumber: string; commDate: string }[] = [];
            previewStartDate.forEach((comm, i) => {
                let dupIndex = orginalComm.findIndex(og => comm.commDate == og.commDate);
                if (dupIndex >= 0) {
                    orginalComm[dupIndex] = { ...comm, rowNumber: `${orginalComm[dupIndex].rowNumber},${comm.rowNumber}` };
                } else {
                    orginalComm = [...orginalComm, { ...comm }];
                }
            });
            return orginalComm;
        }
        return previewStartDate;
    }

    getMulticSchRerunsData(timeZone: Timezone, formData?: BroadcastFormData, userMail?: string): MultiSchedulerData {
        let scheduleRerunData: MultiSchedulerData = {
            ...BROADCAST_MESSAGES.MULTI_SCH_DATA,
            values: []
        };

        scheduleRerunData = {
            ...scheduleRerunData,
            userMail: userMail || '',
            formData: {
                ...scheduleRerunData.formData,
                description: scheduleRerunData.formData.description.replace(
                    '[time_zone]',
                    `${timeZone.timezone_label} (${timeZone.timezone_abbr})`
                )
            }
        };

        let reRunValues = formData?.reruns?.length
            ? formData.reruns.map(run => ({
                  createdBy: run.created_by || '',
                  value: run.schedule_time,
                  status: this.getFormStatusByValue(run.status),
                  disabled: run.status != BroadcastStatus.queued.value
              }))
            : [];
        scheduleRerunData = { ...scheduleRerunData, values: reRunValues };

        if (scheduleRerunData.values.some(schVal => this.isBroadcastFailed(schVal?.status?.value))) {
            scheduleRerunData = {
                ...scheduleRerunData,
                warnNote: BROADCAST_MESSAGES.SCH_DETAIL_RERUN_FAILED_ERR
            };
        }
        return scheduleRerunData;
    }

    /**
     * Track consolidated broadcast form submission event
     * Shared method for both broadcast and welcome message components
     */
    trackBroadcastFormSubmitted(
        analyticService: AnalyticService,
        params: {
            messageType: 'broadcast' | 'welcome';
            formValue: any;
            channelsValue: any;
            languageTranslationForm?: any;
            orgLanguages?: string[];
            tagSelectorSelectedTags?: any[];
            uploadedFile?: File | null;
            generatedMsg?: any;
            aiMessageCopied?: boolean;
            saveStatus?: 'success' | 'failed';
            requestBroadcast: ScheduledSendBroadcast;
            resolverDataBroadcastId?: number;
            filterData?: {
                centers?: string[];
                interestNames?: string[];
                labelNames?: string[];
                languages?: string[];
            };
            tokenTypes?: string[];
        }
    ) {
        const channelsEnabled: string[] = [];
        if (params.channelsValue.sms) channelsEnabled.push('sms');
        if (params.channelsValue.voice) channelsEnabled.push('voice');
        if (params.channelsValue.email) channelsEnabled.push('email');

        const messageText = params.formValue?.message || '';
        const messageEntered = !!messageText;
        const messageLength = messageText.length;

        // Get languages from filter selection
        const selectedLanguages: string[] = params.filterData?.languages || [];

        // Get auto translate disabled languages
        let autoTranslateDisabledLanguages: string[] = [];
        if (params.languageTranslationForm) {
            const translationFormValue = params.languageTranslationForm.value;
            params.orgLanguages?.forEach((lang: string) => {
                const langForm = translationFormValue[lang];
                if (langForm && !langForm.autoTranslate) {
                    autoTranslateDisabledLanguages.push(lang);
                }
            });
        }

        // Get tags with names
        const tagsSelected: number[] = params.tagSelectorSelectedTags?.map(tag => tag.id) || [];
        const tagNames: string[] = params.tagSelectorSelectedTags?.map(tag => tag.name) || [];
        if (params.requestBroadcast.tags) {
            tagsSelected.push(...params.requestBroadcast.tags);
        }
        const uniqueTags = Array.from(new Set(tagsSelected));

        // Get attachment info
        const attachmentUploaded = !!params.uploadedFile || !!params.requestBroadcast.attachmentUrl;
        const attachmentType = params.uploadedFile?.type || '';
        const attachmentFilename = params.uploadedFile?.name || '';

        // Check if AI message was used/copied
        const aiMessageUsed = !!params.generatedMsg;
        const aiMessageCopied = !!params.aiMessageCopied;

        const scheduled = !!params.requestBroadcast.schedule_time?.length;
        const scheduledDate = scheduled && params.requestBroadcast.schedule_time?.[0] 
            ? moment(params.requestBroadcast.schedule_time[0]).toISOString() 
            : '';

        const eventParams: { [key: string]: any } = {
            'message_entered': messageEntered,
            'message_length': messageLength,
            'languages': selectedLanguages,
            'languages_count': selectedLanguages.length,
            'channels_enabled': channelsEnabled,
            'scheduled_date': scheduledDate,
            'schedule_toggle_enabled': !!params.formValue?.isScheduledBroadcast,
            'outreach_type': params.requestBroadcast.parameters?.individual ? 'individual' : 'multiple',
            'tag_name': tagNames,
            'program_name': params.filterData?.centers || [],
            'interest_name': params.filterData?.interestNames || [],
            'label_name': params.filterData?.labelNames || [],
            'token_type': params.tokenTypes || [],
            'attachment_type': attachmentType,
            'attachment_filename': attachmentFilename,
            'ai_message_copied': aiMessageCopied,
            'auto_translate_disabled_languages': autoTranslateDisabledLanguages,
            'save_status': params.saveStatus || 'success',
            'org_id': params.requestBroadcast.orgId,
            'broadcast_id': params.resolverDataBroadcastId
        };

        // Remove undefined/null/empty values
        Object.keys(eventParams).forEach(key => {
            if (eventParams[key] === null || eventParams[key] === undefined || eventParams[key] === '' || 
                (Array.isArray(eventParams[key]) && eventParams[key].length === 0)) {
                delete eventParams[key];
            }
        });

        analyticService.logAnalyticEvent(AnalyticLogLabels.BroadcastFormSubmitted, eventParams);
    }

    /**
     * Validate file using magic byte detection
     * Call this BEFORE uploading the file to check for type mismatches
     */
    validateFileType(file: File): Observable<any> {
        const formData = new FormData();
        formData.append('file', file);

        return this._httpS.validateFileType(formData).pipe(
            map((response: any) => response),
            catchError((error: HttpErrorResponse) => {
                return throwError(error);
            })
        );
    }

    /**
     * Extract personalization tokens from a message
     * Shared utility method for analytics tracking
     */
    extractPersonalizationTokens(message: string): string[] {
        const tokenPatterns: { [key: string]: RegExp } = {
            'firstname': /@firstname/gi,
            'lastname': /@lastname/gi,
            'email': /@email/gi,
            'phoneno': /@phoneno/gi,
            'caregiver_name': /@caregiver_name/gi,
            'caregiver_phoneno': /@caregiver_phoneno/gi,
            'caregiver_email': /@caregiver_email/gi,
            'client_id': /@client_id/gi,
            'member_ref': /@member_ref/gi
        };

        const foundTokens: string[] = [];
        Object.entries(tokenPatterns).forEach(([tokenType, pattern]) => {
            if (pattern.test(message)) {
                foundTokens.push(tokenType);
            }
        });
        return foundTokens;
    }
}
